import { create } from 'zustand';
import type { AdminUser } from '@/domain/model/AdminUser';
import type { AdminUserProfile } from '@/domain/model/AdminUserProfile';
import type { Role } from '@/domain/model/Role';
import { adminService } from '@/infrastructure/store/adminServiceInstance';
import { resolveErrorKey } from '@/infrastructure/util/error';

interface AdminUserState {
  users: AdminUser[];
  userProfile: AdminUserProfile | null;
  isLoading: boolean;
  error: string | null;
}

interface AdminUserActions {
  fetchUsers: (search?: string) => Promise<void>;
  fetchUserProfile: (id: string) => Promise<void>;
  changeUserRole: (id: string, role: Role) => Promise<boolean>;
  deleteUser: (id: string) => Promise<boolean>;
  clearUserProfile: () => void;
  clearError: () => void;
}

export const useAdminUserStore = create<AdminUserState & AdminUserActions>((set, get) => ({
  users: [],
  userProfile: null,
  isLoading: false,
  error: null,

  fetchUsers: async (search?: string) => {
    set({ isLoading: true, error: null });
    try {
      const users = await adminService.getUsers(search);
      set({ users, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'admin.userLoadFailed') });
    }
  },

  fetchUserProfile: async (id: string) => {
    set({ isLoading: true, error: null, userProfile: null });
    try {
      const userProfile = await adminService.getUserProfile(id);
      set({ userProfile, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'admin.userLoadFailed') });
    }
  },

  changeUserRole: async (id: string, role: Role) => {
    set({ isLoading: true, error: null });
    try {
      const updated = await adminService.changeUserRole(id, role);
      set({
        users: get().users.map((u) => (u.id === id ? { ...u, role: updated.role } : u)),
        isLoading: false,
      });
      return true;
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'admin.userSaveFailed') });
      return false;
    }
  },

  deleteUser: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await adminService.deleteUser(id);
      set({ users: get().users.filter((u) => u.id !== id), isLoading: false });
      return true;
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'admin.userDeleteFailed') });
      return false;
    }
  },

  clearUserProfile: () => set({ userProfile: null }),

  clearError: () => set({ error: null }),
}));
